import * as appActions from './actions';
import * as api from '../../api';
import { loadAndApplyLastState } from '../lastState/listeners';
import * as routerActions from '../router/actions';
import { getPermissions } from './selectors';
import * as projectActions from '../project/actions';
import * as categoryActions from '../category/actions';
import * as tagActions from '../tag/actions';
import * as extensionActions from '../extension/actions';
import { getMode, getSelected } from '../finder/selectors';
import * as finderActions from '../finder/actions';
import { Page } from '../router/model';
import * as lastStateActions from '../lastState/actions';
import * as searchActions from '../search/actions';
import * as sortingActions from '../sorting/actions';
import * as viewerActions from '../viewer/actions';
import * as viewModeActions from '../viewMode/actions';
import { getAssetLabLogger } from './logs';
import { getViewMode, getViewModeVisible } from '../viewMode/selectors';
import { ViewMode } from '../viewMode/model';
import { takeLatest, takeEvery, takeOnce, ListenerApi } from '../../listenerMiddleware';
import { Permission } from '../../../general/model';
import { message } from '@myua/kit';
import { notification } from '../../../general/kitMethods';
import { whenAppVisible } from '../../../general/authGate';
import {
  SLOW_NETWORK_MESSAGE,
  SLOW_NETWORK_WARNING_DISMISS_S,
  watchNetworkSpeed,
} from '../../../general/slowNetwork';

export const appListeners = () => {
  init();
  watchPagePathChanged();
  watchPopState();
  watchProjectSelected();
  watchFiltersRelevance();
  watchAssetsFiltered();
  watchCreativeChanged();
  watchLastState();
};

// Замер и перепроверка живут в general/slowNetwork.ts (общее с Creative Hub);
// здесь только показ результата средствами кита (MYUA-16416).
function checkNetworkSpeed(dispatch: ListenerApi['dispatch']) {
  void watchNetworkSpeed({
    onSlowDetected: () => {
      showSlowNetworkWarning();
      dispatch(appActions.slowNetworkChanged({ slow: true }));
    },
    onRestored: () => dispatch(appActions.slowNetworkChanged({ slow: false })),
  });
}

function showSlowNetworkWarning() {
  // Ждём снятия сплеша бутстрапа, а не просто рендера (MYUA-16590). Две причины:
  //
  // 1. Тост antd императивный: его 7-секундный таймер стартует в момент вызова.
  //    `main` диспатчится из bootstrapWithAuth ДО renderApp, а полноэкранный сплеш
  //    (position: fixed, максимальный z-index) висит ещё до 4 секунд, пока
  //    собирается первый экран. Показанный сразу тост почти весь срок жизни проводит
  //    под оверлеем — пользователь видит в лучшем случае хвост. Именно этим Asset Lab
  //    отличался от Creative Hub, где тост лежит в сторе и монтируется (то есть
  //    начинает отсчёт) только вместе с UI.
  // 2. `notification` из kitMethods появляется только после первого рендера
  //    (KitMethodsInitializer), а на медленной сети `onSlowDetected` срабатывает
  //    синхронно внутри dispatch(main()) — то есть до рендера.
  void whenAppVisible().then(() => {
    notification.warning({
      message: 'Slow Network Speed',
      description: SLOW_NETWORK_MESSAGE,
      duration: SLOW_NETWORK_WARNING_DISMISS_S,
    });
  });
}

function init() {
  takeOnce(appActions.main, async (action, { getState, dispatch, signal }) => {
    checkNetworkSpeed(dispatch);

    // Пользователь уже загружен и положен в стор в index.tsx (bootstrap) — до того,
    // как был задиспатчен main(). Здесь его повторно не запрашиваем: к этому моменту
    // права доступа уже в стейте и доступны лисенерам ниже.

    const assetLabLogger = getAssetLabLogger(getState());
    assetLabLogger({ action: 'Service open' });

    await loadAndApplyLastState({ getState, dispatch, signal, initial: true });
    dispatch(appActions.initialized());
  });

  takeOnce(appActions.main, async (action, { dispatch }) => {
    const result = await api.loadSettings();
    if (result.success) {
      dispatch(appActions.settingsLoaded({ settings: result.data.response }));
    } else {
      void message.error('Settings have not been loaded');
    }
  });
}

function watchPagePathChanged() {
  let filtersInitialized = false;

  takeLatest(routerActions.pagePathChanged, (action, { getState, dispatch }) => {
    // popState и pagePathChanged могут происходить как отдельно друг от друга,
    // так и почти одновременно (сначала popState, потом сразу pagePathChanged)

    if (!filtersInitialized) {
      filtersInitialized = true;
      // При первом отображении любой страницы запрашиваем загрузку фильтров
      const permissions = getPermissions(getState());
      if (permissions[Permission['fs.assetlab_access']]) {
        dispatch(projectActions.loadProjectsRequested({ saveLastState: false }));
        dispatch(categoryActions.loadCategoriesRequested({ saveLastState: false }));
        dispatch(tagActions.loadTagsRequested({ saveLastState: false }));
        dispatch(extensionActions.loadExtensionsRequested({ saveLastState: false }));
      }
    }

    const mode = getMode(getState());
    if (mode === null) {
      // Если юзер уходит из finder, то отменяем выбор ассетов
      const selected = getSelected(getState());
      if (selected.ids.length > 0) {
        dispatch(finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }));
      }
      return;
    }

    dispatch(finderActions.loadAssetsRequested());
  });
}

function watchPopState() {
  takeLatest(lastStateActions.popState, async (action, { getState, dispatch, signal }) => {
    // popState и pagePathChanged могут происходить как отдельно друг от друга,
    // так и почти одновременно (сначала popState, потом сразу pagePathChanged)

    const applied = await loadAndApplyLastState({ getState, dispatch, signal, initial: false });
    if (signal.aborted) {
      return;
    }
    if (!applied) {
      return;
    }

    const permissions = getPermissions(getState());
    if (permissions[Permission['fs.assetlab_access']]) {
      dispatch(projectActions.loadProjectsRequested({ saveLastState: false }));
      dispatch(categoryActions.loadCategoriesRequested({ saveLastState: false }));
      dispatch(tagActions.loadTagsRequested({ saveLastState: false }));
      dispatch(extensionActions.loadExtensionsRequested({ saveLastState: false }));
    }

    dispatch(finderActions.loadAssetsRequested());
  });
}

function watchProjectSelected() {
  takeLatest(projectActions.projectIdsSelected, ({ payload }, { getState, dispatch }) => {
    // Когда юзер переключает проекты в сайдбаре, параметры поиска сохраняются,
    // чтобы искать сквозь проекты. Сохранение происходит за счет флага preserveSearch.
    // Переходы, открывающие каталог на конкретном ассете (deep link, reveal
    // из Magic Studio), флаг не передают и поиск не сохраняют. Иначе остаточный
    // поиск мог бы отфильтровать тот ассет, ради которого мы сюда и пришли.
    // При автокоррекции выбора проекта, когда проект пропал из словаря,
    // флаг тоже не передается.
    dispatch(appActions.resetFilters({ preserveSearch: payload.preserveSearch ?? false }));

    const viewMode = getViewMode(getState());
    const viewModeVisible = getViewModeVisible(getState());
    if (viewMode === ViewMode.creatives && !viewModeVisible) {
      dispatch(viewModeActions.changed({ viewMode: ViewMode.assets }));
    }

    const { saveLastState } = payload;

    if (saveLastState) {
      dispatch(lastStateActions.saveRequested());
    }

    dispatch(categoryActions.loadCategoriesRequested({ saveLastState }));
    dispatch(tagActions.loadTagsRequested({ saveLastState }));
    dispatch(extensionActions.loadExtensionsRequested({ saveLastState }));

    const mode = getMode(getState());
    if (mode === Page.viewer) {
      dispatch(finderActions.loadAssetsRequested());
    }
  });
}

function watchFiltersRelevance() {
  takeEvery(categoryActions.categorySelected, ({ payload }, { dispatch }) => {
    dispatch(extensionActions.loadExtensionsRequested({ saveLastState: payload.saveLastState }));
  });

  takeEvery(
    [categoryActions.categorySelected, extensionActions.extensionsSelected],
    ({ payload }, { dispatch }) => {
      dispatch(tagActions.loadTagsRequested({ saveLastState: payload.saveLastState }));
    },
  );

  takeEvery([searchActions.searchRequested, viewModeActions.changed], (action, { dispatch }) => {
    dispatch(tagActions.loadTagsRequested({ saveLastState: true }));
  });
}

function watchAssetsFiltered() {
  takeLatest(
    [
      categoryActions.categorySelected,
      tagActions.tagsSelected,
      extensionActions.extensionsSelected,
      searchActions.searchRequested,
      sortingActions.sortingChanged,
      viewModeActions.changed,
    ],
    (action, { dispatch }) => {
      dispatch(finderActions.loadAssetsRequested());
    },
  );
}

function watchCreativeChanged() {
  takeEvery(viewerActions.creativeChanged, ({ payload }, { dispatch }) => {
    if (payload.loadAssets) {
      dispatch(finderActions.loadAssetsRequested());
    }
  });
}

function watchLastState() {
  takeEvery(appActions.sidebarExpandedChanged, (action, { dispatch }) => {
    dispatch(lastStateActions.saveRequested());
  });
}
